import React, { useEffect, useState, Fragment } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { t } from '@lingui/macro';
import inactivityImage from '../../assets/images/error-boy.gif';
import timeUpImage from '../../assets/images/time-up.svg';
import css from './time-ticker.scss';
import { useTimer } from './time-ticker-hooks';
import { useIdleTimer } from './use-idle-timer';
import { usePageExit } from './use-page-exit';
import { handInQtc, saveTimeElapsed, sendTimeElapsed } from '../mle/mle-actions';
import { ModalGeneric } from '../../components/modal-content';
import { constructModal, activateModal } from '../modal/modal-actions';
import { useLocalisation } from '../../helpers/i18n/use-localisation';
import { countAnswered } from '../progress-info/progress-info-utils';
import { leaveMle } from '../../helpers/utils';
import { INACTIVITY_TIMEOUT_MS } from '../../helpers/constants';

const TimeTicker = ({
  className
}) => {
  const {
    timeLeftInSeconds,
    isCompleted,
    questions
  } = useSelector(state => state.mleReducer);
  const dispatch = useDispatch();
  const { i18n } = useLocalisation();
  const [isInactivityModalOpen, setIsInactivityModalOpen] = useState(false);
  const [isExpired, setIsExpired] = useState(false);

  const [tick, getSecondsElapsedSinceLastSave, markSecondsElapsedSaved] = useTimer({
    timeLeft: timeLeftInSeconds,
    isCompleted,
    isPaused: isInactivityModalOpen
  });

  const expirationModal = () => {
    const isAssessmentAttempted = countAnswered(questions) > 0;

    dispatch(handInQtc());

    const modalData = {
      subHeader: (
        <Fragment>
          <img className={css['timeout-image']} src={timeUpImage} alt="" />
          {i18n._(t`Time's up`)}
        </Fragment>
      ),
      paragraph: i18n._(
        isAssessmentAttempted ?
          t`Your assessment has been submitted.` :
          t`Your assessment has been submitted. As no questions were answered, your score is 0%.`
      ),
      buttons: [
        {
          text: i18n._(t`Done`),
          callBack: () => dispatch(activateModal(false))
        }
      ]
    };

    dispatch(constructModal({
      activate: true,
      content: <ModalGeneric modalData={modalData} variant="timeout" />,
      canvasOnClick: false
    }));
  };

  const continueAssessment = () => {
    dispatch(activateModal(false));
    setIsInactivityModalOpen(false);
  };

  const returnToTodoList = () => {
    dispatch(activateModal(false));
    leaveMle('Inactivity modal - return to to-do list');
  };

  const showInactivityModal = () => {
    const secondsElapsed = getSecondsElapsedSinceLastSave();

    setIsInactivityModalOpen(true);
    dispatch(saveTimeElapsed(secondsElapsed)).then((isSaved) => {
      if (isSaved) {
        markSecondsElapsedSaved(secondsElapsed);
      }
    });

    const modalData = {
      subHeader: (
        <Fragment>
          <img className={css['inactivity-image']} src={inactivityImage} alt="" />
          {i18n._(t`Are you still there?`)}
        </Fragment>
      ),
      paragraph: i18n._(
        t`Your timer is paused. Would you like to continue your assessment or return to your To-do list?`
      ),
      buttons: [
        {
          text: i18n._(t`Return to To-do list`),
          callBack: returnToTodoList,
          type: 'secondary'
        },
        {
          text: i18n._(t`Continue assessment`),
          callBack: continueAssessment,
          type: 'primary'
        }
      ]
    };

    dispatch(constructModal({
      activate: true,
      content: <ModalGeneric modalData={modalData} variant="timeout" />,
      canvasOnClick: false
    }));
  };

  const saveTimeElapsedOnExit = () => {
    const secondsElapsed = getSecondsElapsedSinceLastSave();

    if (secondsElapsed <= 0) {
      return;
    }

    sendTimeElapsed(secondsElapsed);
    markSecondsElapsedSaved(secondsElapsed);
  };

  usePageExit(saveTimeElapsedOnExit);

  useIdleTimer({
    timeoutMs: INACTIVITY_TIMEOUT_MS,
    isPaused: isCompleted || isExpired || isInactivityModalOpen,
    onIdle: showInactivityModal
  });

  useEffect(() => {
    if (tick === '00:00' && !isCompleted && !isExpired) {
      setIsExpired(true);
      expirationModal();
    }
  }, [tick]);

  return (
    <div className={classNames(css['time-ticker'], className)}>
      {tick}
    </div>
  );
};

TimeTicker.propTypes = {
  className: PropTypes.string
};

export default TimeTicker;
